/*
 * CAPTURE-OWNED MODULE
 * TestRunPanel: Displays template test-run diagnostic results.
 * Shows per-field extraction results with success/failure indicators,
 * anchor match details, failure reasons, and line item results.
 */
import React, { useState } from 'react';
import './TestRunPanel.css';

const TestRunPanel = ({ result, loading, error, onClose, onFieldClick, onRerun, fieldLabels = {} }) => {
  const [expandedField, setExpandedField] = useState(null);
  const [lineItemsExpanded, setLineItemsExpanded] = useState(false);

  if (loading) {
    return (
      <div className="test-run-panel">
        <div className="test-run-header">
          <h4>Test Run</h4>
          <button className="btn-icon" onClick={onClose} title="Close">✕</button>
        </div>
        <div className="test-run-loading">
          <div className="spinner" />
          <p>Running extraction test…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="test-run-panel">
        <div className="test-run-header">
          <h4>Test Run</h4>
          <button className="btn-icon" onClick={onClose} title="Close">✕</button>
        </div>
        <div className="test-run-error">
          <p>Test failed: {error}</p>
        </div>
      </div>
    );
  }

  if (!result) return null;

  const { summary, regionValidation, classification, fields, lineItems, lineItemDiagnostics, ms } = result;

  // Compute grade color
  const rate = summary.extractionRate;
  const gradeColor = rate >= 80 ? '#22c55e' : rate >= 50 ? '#f59e0b' : '#ef4444';
  const gradeLabel = rate >= 80 ? 'Good' : rate >= 50 ? 'Fair' : 'Poor';

  return (
    <div className="test-run-panel">
      {/* Header */}
      <div className="test-run-header">
        <h4>Test Run Results</h4>
        <span className="test-run-time">{ms}ms</span>
        <button className="btn-icon" onClick={onClose} title="Close">✕</button>
      </div>

      {/* Summary Card */}
      <div className="test-run-summary" style={{ borderLeftColor: gradeColor }}>
        <div className="summary-grade" style={{ color: gradeColor }}>
          {summary.extractionRate}% <span className="grade-label">{gradeLabel}</span>
        </div>
        <div className="summary-stats">
          <span className="stat">
            <strong>{summary.extractedCount}</strong>/{summary.totalFields} fields
          </span>
          <span className="stat">
            <strong>{summary.lineItemCount}</strong> line items
          </span>
        </div>
        {classification && (
          <div className="summary-classification">
            Type: <strong>{classification.documentType}</strong> ({(classification.confidence * 100).toFixed(0)}%)
          </div>
        )}
        {onRerun && (
          <button className="btn-rerun" onClick={onRerun}>
            ▶ Re-run Test
          </button>
        )}
      </div>

      {/* Region Validation */}
      {regionValidation && (
        <div className={`test-run-section test-run-regions ${regionValidation.valid ? 'valid' : 'invalid'}`}>
          <div className="section-header">
            <span className={`status-dot ${regionValidation.valid ? 'dot-green' : 'dot-yellow'}`} />
            Region check
            <span
              className="region-info"
              title="Regions are page areas the template maps (header, line items, totals…). Re-run Detect Regions or adjust the region if those fields extract poorly."
            >
              ⓘ
            </span>
          </div>
          {regionValidation.valid ? (
            <div className="region-status-ok">
              All {regionValidation.foundRegions.length} areas located.
            </div>
          ) : (
            <div className="region-status-warn">
              {regionValidation.foundRegions.length} of {regionValidation.foundRegions.length + regionValidation.missingRegions.length} areas
              located. {regionValidation.missingRegions.join(', ')} not found on this sample — fields in
              that area may be less reliable across layouts.
            </div>
          )}
        </div>
      )}

      {/* Per-Field Results */}
      <div className="test-run-section">
        <div className="section-header">Fields</div>
        <div className="field-results">
          {fields.map((f) => {
            const label = fieldLabels[f.field_key] || f.field_key;
            const isExpanded = expandedField === f.field_key;
            const anchorMissWarn = f.success && f.anchor_text && !f.anchor_found;
            // "more" reveals anchor match/miss, transform trace, and the anchor-position warning.
            const hasDetail =
              f.anchor_text != null ||
              (f.transform_trace && f.transform_trace.length > 0);
            // Success rows always expand (they gain an "Open in zone editor" action);
            // failing rows expand only when they carry anchor/transform detail.
            const canExpand = hasDetail || f.success;

            return (
            <div
              key={f.field_key}
              className={`field-result ${f.success ? 'field-success' : 'field-failed'}`}
              onClick={() => canExpand && setExpandedField(isExpanded ? null : f.field_key)}
              title={f.success ? `Extracted: "${f.value}"` : f.failure_reason || 'Failed'}
            >
              <div className="field-result-row">
                <span className={`status-dot ${f.success ? 'dot-green' : 'dot-red'}`} />
                <span className="field-result-label">{label}</span>

                {f.success ? (
                  <span className="field-result-value" title={f.value_text || f.value}>
                    {String(f.value_text || f.value || '').substring(0, 40)}
                    {String(f.value_text || f.value || '').length > 40 ? '…' : ''}
                  </span>
                ) : (
                  <span className="field-result-empty">—</span>
                )}
                <span className="field-result-conf">
                  {f.confidence != null ? `${(f.confidence * 100).toFixed(0)}%` : ''}
                </span>
                {canExpand && (
                  <button
                    className={`field-more-toggle ${isExpanded ? 'expanded' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedField(isExpanded ? null : f.field_key);
                    }}
                    title={isExpanded ? 'Hide details' : 'Show details'}
                    aria-expanded={isExpanded}
                  >
                    ▸
                  </button>
                )}
              </div>

              {/* Failure reason + Fix action — kept inline (not behind "more") */}
              {!f.success && f.failure_reason && (
                <div className="field-failure-reason">
                  {f.failure_reason}
                  <button
                    className="btn-fix-field"
                    onClick={(e) => {
                      e.stopPropagation();
                      onFieldClick && onFieldClick(f.field_key);
                    }}
                  >
                    Select Zone to Fix →
                  </button>
                </div>
              )}

              {/* Collapsible diagnostic detail — hidden until "more" is expanded */}
              {isExpanded && (
                <div className="field-detail">
                  {/* Anchor-position warning message */}
                  {anchorMissWarn && (
                    <div className="field-anchor-warning-msg">
                      <span className="field-anchor-warning" title="Extracted from field zone position — anchor label not matched">⚠</span>{' '}
                      Value extracted from field zone position. The anchor label "{f.anchor_text}" was not matched,
                      so the system used the zone's drawn position instead of anchor-relative positioning.
                      This works for this document but may be less reliable across layout variations.
                    </div>
                  )}

                  {/* Anchor diagnostics */}
                  {f.anchor_text != null && (
                    <div className="field-anchor-info">
                      <span className="anchor-label">Anchor:</span>
                      {f.anchor_found ? (
                        <span className="anchor-match">
                          ✓ "{f.anchor_match_text}" ({f.anchor_match_type})
                        </span>
                      ) : (
                        <span className="anchor-miss">
                          ✗ "{f.anchor_text}" not found
                        </span>
                      )}
                    </div>
                  )}

                  {/* Transform trace */}
                  {f.transform_trace && f.transform_trace.length > 0 && (
                    <div className="field-transforms">
                      {f.transform_trace.map((t, i) => (
                        <span key={i} className="transform-step">
                          {t.type}: "{t.before}" → "{t.after}"
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Explicit navigate — success rows only. Failures use the
                      inline "Select Zone to Fix →" button instead. */}
                  {f.success && onFieldClick && (
                    <button
                      className="btn-open-zone"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFieldClick(f.field_key);
                      }}
                    >
                      Open in zone editor →
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* Line Items */}
      {lineItems && lineItems.length > 0 && (
        <div className="test-run-section">
          <button
            className={`li-toggle section-header ${lineItemsExpanded ? 'expanded' : ''}`}
            onClick={() => setLineItemsExpanded((v) => !v)}
            aria-expanded={lineItemsExpanded}
          >
            <span className="li-toggle-caret">{lineItemsExpanded ? '▾' : '▸'}</span>
            Line Items ({lineItems.length})
            {lineItemDiagnostics && (
              <span className="li-source"> via {lineItemDiagnostics.source}</span>
            )}
          </button>
          {lineItemsExpanded && (
          <table className="line-items-table">
            <thead>
              <tr>
                <th>#</th>
                <th>SKU</th>
                <th>Qty</th>
                <th>Description</th>
                <th>Unit</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li) => (
                <tr key={li.index}>
                  <td>{li.index + 1}</td>
                  <td className="li-sku" title={li.sku}>{li.sku || '—'}</td>
                  <td>{li.qty ?? '—'}</td>
                  <td className="li-desc" title={li.description}>{(li.description || '—').substring(0, 30)}</td>
                  <td>{li.unitPrice != null ? `$${Number(li.unitPrice).toFixed(2)}` : '—'}</td>
                  <td>{li.totalPrice != null ? `$${Number(li.totalPrice).toFixed(2)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      )}

      {lineItemDiagnostics && lineItems.length === 0 && (
        <div className="test-run-section">
          <div className="section-header">Line Items</div>
          <div className="field-failure-reason">
            No line items extracted. {lineItemDiagnostics.source === 'error'
              ? `Error: ${lineItemDiagnostics.error}`
              : `Tables detected: ${lineItemDiagnostics.tablesDetected}, zone used: ${lineItemDiagnostics.zoneUsed}`}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestRunPanel;
